/* ------------------------------------------------------------------ */
/*  Course data — shared across landing page, course page, dashboard  */
/* ------------------------------------------------------------------ */

import { z } from 'zod';
import rawData from './courseData.json';

export interface Lesson {
  id: string;
  moduleNumber: string;
  number: string;
  slug: string;
  title: string;
  description: string;
  duration: string;
  durationSeconds: number;
  preview?: boolean;
}

export interface Module {
  number: string;
  title: string;
  slug: string;
  lessons: Lesson[];
}

/* ------------------------------------------------------------------ */
/*  JSON SCHEMA (validated at import time)                             */
/* ------------------------------------------------------------------ */

const RawLessonSchema = z.object({
  number: z.string(),
  title: z.string(),
  description: z.string().optional(),
  duration: z.string(),
  preview: z.boolean().optional(),
});

const RawModuleSchema = z.object({
  number: z.string(),
  title: z.string(),
  lessons: z.array(RawLessonSchema),
});

const CourseDataSchema = z.object({
  modules: z.array(RawModuleSchema),
});

const { modules: rawModules } = CourseDataSchema.parse(rawData);

/* ------------------------------------------------------------------ */
/*  COURSE META                                                        */
/* ------------------------------------------------------------------ */

export const course = {
  slug: 'modern-web-development' as const,
  title: 'Modern Web Development',
  description:
    'A practical course for the education sector — modern web development for teachers, students, and schools.',
  priceCents: 2000,
  thumbnailUrl: '/course-thumbnail.webp',
};

/* ------------------------------------------------------------------ */
/*  HELPERS                                                            */
/* ------------------------------------------------------------------ */

function slugify(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

function parseDurationSeconds(hms: string): number {
  const [mm, ss] = hms.split(':').map(Number);
  return mm * 60 + ss;
}

/* ------------------------------------------------------------------ */
/*  DERIVED DATA                                                       */
/* ------------------------------------------------------------------ */

export const modules: Module[] = rawModules.map((mod) => ({
  number: mod.number,
  title: mod.title,
  slug: slugify(mod.title),
  lessons: [...mod.lessons]
    .sort((a, b) => {
      const [, aLesson = "0"] = a.number.split(".");
      const [, bLesson = "0"] = b.number.split(".");
      return parseInt(aLesson, 10) - parseInt(bLesson, 10);
    })
    .map((lesson) => ({
    id: `video-${lesson.number}`,
    moduleNumber: mod.number,
    number: lesson.number,
    slug: slugify(lesson.title),
    title: lesson.title,
    description: lesson.description ?? 'Coming soon.',
    duration: lesson.duration,
    durationSeconds: parseDurationSeconds(lesson.duration),
    preview: lesson.preview,
  })),
}));

export const allLessons: Lesson[] = modules.flatMap((m) => m.lessons);

export const totalLessons = allLessons.length;

export const totalMinutes = modules.reduce((acc, m) => {
  return acc + m.lessons.reduce((a, l) => a + l.durationSeconds / 60, 0);
}, 0);

/* ------------------------------------------------------------------ */
/*  SLUG UNIQUENESS ASSERTION                                          */
/* ------------------------------------------------------------------ */

const slugSet = new Set<string>();
for (const lesson of allLessons) {
  if (slugSet.has(lesson.slug)) {
    throw new Error(`Duplicate lesson slug detected: ${lesson.slug}`);
  }
  slugSet.add(lesson.slug);
}

/* ------------------------------------------------------------------ */
/*  EXPORTS                                                            */
/* ------------------------------------------------------------------ */

export function formatDuration(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m.toString().padStart(2, '0')}m`;
}

export function moduleDuration(lessons: Lesson[]): string {
  const totalSeconds = lessons.reduce((acc, l) => acc + l.durationSeconds, 0);
  return formatDuration(Math.round(totalSeconds / 60));
}

export function getLessonBySlug(slug: string): Lesson | undefined {
  return allLessons.find((l) => l.slug === slug);
}

export function getAdjacentLessons(slug: string): {
  prev?: Lesson;
  next?: Lesson;
} {
  const idx = allLessons.findIndex((l) => l.slug === slug);
  if (idx === -1) return {};
  return {
    prev: allLessons[idx - 1],
    next: allLessons[idx + 1],
  };
}
